import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apartmentsAPI, propertiesAPI } from '../services/api';
import Modal from '../components/common/Modal';
import { toast } from '../components/common/Toast';
import { Plus, Search, BedDouble, Bath, RefreshCw } from 'lucide-react';
import { fmt, statusLabel, statusClass, errMsg } from '../utils/helpers';
import { usePermissions } from '../context/PermissionsContext';

const EMPTY = { propertyId:'', unit:'', floor:'', bedrooms:'1', bathrooms:'1', sqft:'', rent:'', status:'vacant', features:'' };

export default function Apartments() {
  const [params] = useSearchParams();
  const { t }    = useTranslation();
  const { can }  = usePermissions();
  const canCreate = can('apartments', 'create');
  const canEdit   = can('apartments', 'edit');
  const [apts, setApts]       = useState([]);
  const [props, setProps]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [propFilter, setPropFilter] = useState(params.get('property') || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal]     = useState(false);
  const [edit, setEdit]       = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const q = {};
    if (propFilter)    q.propertyId = propFilter;
    if (statusFilter)  q.status     = statusFilter;
    Promise.all([apartmentsAPI.getAll(q), propertiesAPI.getAll()])
      .then(([a,p]) => { setApts(a.data.data); setProps(p.data.data); })
      .catch(()=>toast.error(t('error')))
      .finally(()=>setLoading(false));
  }, [propFilter, statusFilter, t]);

  useEffect(()=>{ load(); },[load]);

  const filtered = apts.filter(a => !search || a.unit.toLowerCase().includes(search.toLowerCase()) || (a.tenantId?.name||'').toLowerCase().includes(search.toLowerCase()));

  const openAdd  = () => { setForm({...EMPTY, propertyId: props[0]?._id||''}); setEdit(null); setModal(true); };
  const openEdit = a  => { setForm({...a, propertyId: a.propertyId?._id||a.propertyId, features:(a.features||[]).join(', ')}); setEdit(a._id); setModal(true); };

  const handleSave = async () => {
    if (!form.unit || !form.propertyId) return toast.warning(t('unit') + ' ' + t('property') + ' ' + t('required'));
    setSaving(true);
    try {
      const data = { ...form, floor:Number(form.floor)||1, bedrooms:Number(form.bedrooms), bathrooms:Number(form.bathrooms), sqft:Number(form.sqft)||0, rent:Number(form.rent)||0, features:form.features?form.features.split(',').map(s=>s.trim()).filter(Boolean):[] };
      if (edit) { await apartmentsAPI.update(edit,data); toast.success(t('apartmentUpdated')); }
      else       { await apartmentsAPI.create(data);      toast.success(t('apartmentCreated')); }
      setModal(false); load();
    } catch(err){ toast.error(errMsg(err)); }
    finally{ setSaving(false); }
  };

  const STATUSES = ['','occupied','vacant','maintenance'];

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">{t('apartments')}</h1><p className="page-subtitle">{filtered.length} {t('apartmentsInPortfolio')}</p></div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-secondary btn-icon" onClick={load}><RefreshCw size={16}/></button>
          {canCreate && <button className="btn btn-primary" onClick={openAdd}><Plus size={16}/>{t('addApartment')}</button>}
        </div>
      </div>

      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <div className="search-bar" style={{ flex:1, minWidth:200 }}>
          <Search size={15} color="var(--ink-20)"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={t('searchApartments')}/>
        </div>
        <select className="form-input form-select" style={{ flex:'1 1 160px', minWidth:120 }} value={propFilter} onChange={e=>setPropFilter(e.target.value)}>
          <option value="">{t('allProperties')}</option>
          {props.map(p=><option key={p._id} value={p._id}>{p.name}</option>)}
        </select>
        <div className="tabs">
          {STATUSES.map(s=><div key={s} className={`tab${statusFilter===s?' active':''}`} onClick={()=>setStatusFilter(s)}>{s?statusLabel(s):'All'}</div>)}
        </div>
      </div>

      <div className="card">
        <div style={{ overflowX:'auto' }}>
          {loading ? <div style={{ padding:40, textAlign:'center' }}><div className="spinner" style={{ margin:'0 auto' }}/></div> : (
            <table className="data-table">
              <thead><tr><th style={{ width:1,whiteSpace:'nowrap' }}></th><th>{t('unit')}</th><th>{t('property')}</th><th>{t('layout')}</th><th>{t('sqft')}</th><th>{t('rent')}/mo</th><th>{t('status')}</th><th>{t('tenant')}</th><th>{t('features')}</th></tr></thead>
              <tbody>
                {filtered.map(a=>(
                  <tr key={a._id}>
                    <td className="table-actions">{canEdit && <button className="btn btn-secondary btn-sm" onClick={()=>openEdit(a)}>{t('edit')}</button>}</td>
                    <td><div style={{ fontWeight:700, fontSize:15 }}>#{a.unit}</div><div style={{ fontSize:11,color:'var(--ink-40)' }}>{t('floor')} {a.floor||1}</div></td>
                    <td style={{ fontSize:13 }}>{a.propertyId?.name||'—'}</td>
                    <td><div style={{ display:'flex', gap:10 }}>
                      <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:13 }}><BedDouble size={13} color="var(--ink-40)"/>{a.bedrooms}BR</span>
                      <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:13 }}><Bath size={13} color="var(--ink-40)"/>{a.bathrooms}BA</span>
                    </div></td>
                    <td style={{ fontSize:13 }}>{fmt.number(a.sqft)}</td>
                    <td><span style={{ fontWeight:700 }}>{fmt.currency(a.rent)}</span></td>
                    <td><span className={`badge ${statusClass(a.status)}`}>{statusLabel(a.status)}</span></td>
                    <td>{a.tenantId?<div><div style={{ fontSize:13,fontWeight:500 }}>{a.tenantId.name}</div><div style={{ fontSize:11,color:'var(--ink-40)' }}>{a.tenantId.email}</div></div>:<span style={{ color:'var(--ink-20)',fontSize:13 }}>{t('vacant')}</span>}</td>
                    <td><div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>{(a.features||[]).slice(0,2).map(f=><span key={f} className="badge badge-neutral">{f}</span>)}</div></td>
                  </tr>
                ))}
                {filtered.length===0&&<tr><td colSpan={9} style={{ textAlign:'center',padding:40,color:'var(--ink-40)' }}>{t('noApartmentsFound')}</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal open={modal} onClose={()=>setModal(false)} title={edit?t('editApartment'):t('addApartmentTitle')}
        footer={<><button className="btn btn-secondary" onClick={()=>setModal(false)}>{t('cancel')}</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?t('saving'):t('saveApartment')}</button></>}>
        <div className="form-grid form-grid-2" style={{ gap:16 }}>
          <div className="form-group"><label className="form-label">{t('property')}</label><select className="form-input form-select" value={form.propertyId} onChange={e=>setForm(f=>({...f,propertyId:e.target.value}))}><option value="">{t('select')}</option>{props.map(p=><option key={p._id} value={p._id}>{p.name}</option>)}</select></div>
          <div className="form-group"><label className="form-label">{t('unit')} #</label><input className="form-input" value={form.unit} onChange={e=>setForm(f=>({...f,unit:e.target.value}))} placeholder="101"/></div>
          <div className="form-group"><label className="form-label">{t('floor')}</label><input className="form-input" type="number" value={form.floor} onChange={e=>setForm(f=>({...f,floor:e.target.value}))} placeholder="1"/></div>
          <div className="form-group"><label className="form-label">{t('status')}</label><select className="form-input form-select" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>{['vacant','occupied','maintenance','offline'].map(s=><option key={s} value={s}>{statusLabel(s)}</option>)}</select></div>
          <div className="form-group"><label className="form-label">{t('bedrooms')}</label><select className="form-input form-select" value={form.bedrooms} onChange={e=>setForm(f=>({...f,bedrooms:e.target.value}))}>{[0,1,2,3,4].map(n=><option key={n} value={n}>{n} BR</option>)}</select></div>
          <div className="form-group"><label className="form-label">{t('bathrooms')}</label><select className="form-input form-select" value={form.bathrooms} onChange={e=>setForm(f=>({...f,bathrooms:e.target.value}))}>{[1,1.5,2,2.5,3].map(n=><option key={n} value={n}>{n} BA</option>)}</select></div>
          <div className="form-group"><label className="form-label">{t('sqft')}</label><input className="form-input" type="number" value={form.sqft} onChange={e=>setForm(f=>({...f,sqft:e.target.value}))} placeholder="850"/></div>
          <div className="form-group"><label className="form-label">{t('monthlyRent')}</label><input className="form-input" type="number" value={form.rent} onChange={e=>setForm(f=>({...f,rent:e.target.value}))} placeholder="1500"/></div>
          <div className="form-group" style={{ gridColumn:'1/-1' }}><label className="form-label">{t('features')} ({t('commaSeparated')})</label><input className="form-input" value={form.features} onChange={e=>setForm(f=>({...f,features:e.target.value}))} placeholder="Balcony, City View, Fireplace"/></div>
        </div>
      </Modal>
    </div>
  );
}
